import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useDevProOverride, PRO_OVERRIDE_MODES } from "@/contexts/DevProOverrideContext";

const SubscriptionContext = createContext(null);

export const useSubscription = () => useContext(SubscriptionContext);

export const SubscriptionProvider = ({ children }) => {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // ============================================================================
  // DEV-ONLY: Pro Access Override Support
  // This allows developers to test Pro-gated features without real subscriptions
  // ============================================================================
  const { isDevMode, overrideMode, getEffectiveProStatus, getRequestHeaders } = useDevProOverride();

  const fetchSubscription = useCallback(async () => {
    if (!user) {
      setSubscription(null);
      setLoading(false);
      return;
    }
    try {
      const headers = getRequestHeaders();
      const response = await apiClient.get("/subscription", { headers });
      setSubscription(response.data);
    } catch (error) {
      // User might not be authenticated
      setSubscription(null);
    } finally {
      setLoading(false);
    }
  }, [user, getRequestHeaders]);

  // Re-fetch subscription when user changes (login/logout) or override changes
  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription, user, overrideMode]);

  // ============================================================================
  // Apply dev override to Pro status if active
  // ============================================================================
  const actualIsPro = subscription?.tier === "pro";
  const actualIsTrial = subscription?.is_trial || false;
  
  // Whitelist / has_pro_access from backend (central authority)
  const backendHasProAccess = subscription?.has_pro_access || false;
  const backendIsWhitelisted = subscription?.is_whitelisted || false;
  
  // Get effective status (respects dev override)
  const { isPro: effectiveIsPro, isTrial: effectiveIsTrial, isOverridden } = 
    getEffectiveProStatus(actualIsPro || backendHasProAccess, actualIsTrial);
  
  // True Pro subscriber (paid or whitelisted) - respects override
  const isPro = effectiveIsPro;
  
  // Has Pro features (either paid, whitelisted, or in trial) - respects override
  const hasProFeatures = effectiveIsPro || effectiveIsTrial;
  
  // Whitelist status
  const isWhitelisted = backendIsWhitelisted;
  
  // Trial info - override can suppress trial
  const isTrial = isOverridden ? effectiveIsTrial : (subscription?.is_trial || false);
  const trialDaysRemaining = isOverridden ? 0 : (subscription?.trial_days_remaining || 0);
  const trialEndsAt = isOverridden ? null : subscription?.trial_ends_at;
  
  const canAccessFeature = useCallback((feature) => {
    if (isDevMode && overrideMode !== PRO_OVERRIDE_MODES.NORMAL) {
      if (overrideMode === PRO_OVERRIDE_MODES.FORCE_PRO) return true;
      if (overrideMode === PRO_OVERRIDE_MODES.FORCE_FREE) {
        const proFeatures = ["grocery_list", "7_day_plans", "unlimited_swaps", 
                           "consistent_meals", "fitness_tracker", "meal_delivery"];
        return !proFeatures.includes(feature);
      }
    }
    if (!subscription) return false;
    // Whitelisted users always have full access
    if (isWhitelisted) return true;
    if (isTrial) return true;
    switch (feature) {
      case "grocery_list":
      case "7_day_plans":
      case "unlimited_swaps":
      case "consistent_meals":
      case "fitness_tracker":
      case "meal_delivery":
        return isPro;
      default:
        return true;
    }
  }, [isDevMode, overrideMode, subscription, isWhitelisted, isTrial, isPro]);

  const swapsRemaining = useCallback(() => {
    if (!subscription) return 0;
    if (isPro || isTrial) return -1;
    return subscription.swaps_remaining ?? 3;
  }, [subscription, isPro, isTrial]);

  const maxMealPlanDays = useCallback(() => {
    if (!subscription) return 3;
    return (isPro || isTrial) ? 7 : 3;
  }, [subscription, isPro, isTrial]);

  const shouldShowUpgrade = useCallback((action) => {
    if (isDevMode && overrideMode !== PRO_OVERRIDE_MODES.NORMAL) {
      if (overrideMode === PRO_OVERRIDE_MODES.FORCE_PRO) return false;
      if (overrideMode === PRO_OVERRIDE_MODES.FORCE_FREE) {
        const proActions = [
          "7_day_plans", "swap_limit", "consistent_meals",
          "fitness_tracker", "meal_delivery"
        ];
        return proActions.includes(action);
      }
    }
    if (isPro) return false;
    if (isWhitelisted) return false;
    if (isTrial) return false;
    const proActions = [
      "7_day_plans", "swap_limit", "consistent_meals",
      "fitness_tracker", "meal_delivery"
    ];
    return proActions.includes(action);
  }, [isDevMode, overrideMode, isPro, isWhitelisted, isTrial]);

  const value = useMemo(() => ({
    subscription,
    loading,
    isPro,
    hasProFeatures,
    isTrial,
    isWhitelisted,
    trialDaysRemaining,
    trialEndsAt,
    canAccessFeature,
    swapsRemaining,
    maxMealPlanDays,
    shouldShowUpgrade,
    refresh: fetchSubscription,
    // ============================================================================
    // DEV-ONLY: Expose override status for UI indicators
    // ============================================================================
    isDevOverrideActive: isDevMode && overrideMode !== PRO_OVERRIDE_MODES.NORMAL,
    devOverrideMode: overrideMode,
  }), [subscription, loading, isPro, hasProFeatures, isTrial, isWhitelisted, trialDaysRemaining, trialEndsAt,
       canAccessFeature, swapsRemaining, maxMealPlanDays, shouldShowUpgrade, fetchSubscription,
       isDevMode, overrideMode]);

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionContext;
